import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FaqAccordion from './components/FaqAccordion';
import { openSaleSmartlyChat } from '@/lib/salesmartly';

type NavKey = 'home' | 'order' | 'pay' | 'result' | 'usdt' | 'refund' | 'contact' | 'referral';

interface NavItem {
  key: NavKey;
  label: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { key: 'home',     label: '帮助首页',    icon: 'ri-home-4-line' },
  { key: 'order',    label: '如何下单',    icon: 'ri-file-add-line' },
  { key: 'pay',      label: '如何付款',    icon: 'ri-bank-card-line' },
  { key: 'result',   label: '如何查看结果', icon: 'ri-file-search-line' },
  { key: 'usdt',     label: '如何购买USDT', icon: 'ri-coin-line' },
  { key: 'refund',   label: '如何退款',    icon: 'ri-refund-2-line' },
  { key: 'contact',  label: '如何联系客服', icon: 'ri-customer-service-2-line' },
  { key: 'referral', label: '如何推广赚钱', icon: 'ri-gift-line' },
];

const QUICK_CARDS = [
  { key: 'order',    label: '如何下单',    desc: '了解如何选择服务并提交查询申请', icon: 'ri-file-add-line',       color: 'bg-slate-50' },
  { key: 'pay',      label: '如何付款',    desc: '查看支持的支付方式和充值流程',   icon: 'ri-bank-card-line',      color: 'bg-slate-50' },
  { key: 'result',   label: '如何查看结果', desc: '了解如何查看和下载查询结果',    icon: 'ri-file-search-line',    color: 'bg-slate-50' },
  { key: 'usdt',     label: '如何购买USDT', desc: '学习如何购买和使用USDT充值',   icon: 'ri-coin-line',           color: 'bg-slate-50' },
  { key: 'refund',   label: '如何退款',    desc: '了解退款政策和申请流程',        icon: 'ri-refund-2-line',       color: 'bg-slate-50' },
  { key: 'referral', label: '如何推广赚钱', desc: '了解推广返佣计划和提现方式',   icon: 'ri-gift-line',           color: 'bg-slate-50' },
];

const FAQ_ITEMS = [
  { q: '查询结果多久能出来？', a: '支付完成后系统会立即开始处理您的订单。简单查询通常 5–15 分钟完成，复杂查询（如聊天记录、出入境记录）可能需要 2–6 小时，具体处理时间请查看对应服务说明。' },
  { q: '支持哪些支付方式？', a: '我们支持账户余额支付、USDT 加密货币支付、以及联系客服人工支付三种方式。推荐使用账户余额支付，充值后即可快速下单，更加便捷。' },
  { q: '如何申请退款？', a: '进入"我的订单"找到对应订单，点击"申请退款"按钮，填写退款原因并提交。我们会在 1–3 个工作日内审核您的申请，审核通过后退款将原路返回。' },
  { q: '查询结果准确吗？', a: '我们的数据来源于官方渠道和专业数据合作方，准确率在 95% 以上。如因数据原因导致查询失败或结果不准确，可申请退款，我们承诺查不到退款。' },
  { q: '我的查询信息会泄露吗？', a: '绝对不会。我们采用端对端加密传输，所有查询记录仅您本人可见，服务器不留存任何敏感信息，请放心使用。' },
  { q: '如何联系人工客服？', a: '您可以通过页面右下角的在线客服按钮联系我们，也可以通过 Telegram 或微信联系客服团队，我们 7×24 小时在线为您服务。' },
  { q: '可以查询境外手机号吗？', a: '目前我们主要支持中国大陆手机号及相关信息查询，部分服务支持港澳台号码，境外号码暂不支持，具体以各服务说明为准。' },
  { q: '推广返佣如何提现？', a: '推广佣金累计满 100 元即可申请提现，支持 USDT、银行转账等方式，通常 1–2 个工作日到账。详情请查看推广赚钱页面。' },
];

const CONTENT_MAP: Record<NavKey, { title: string; icon: string; steps: { title: string; desc: string }[] }> = {
  home: { title: '', icon: '', steps: [] },
  order: {
    title: '如何下单',
    icon: 'ri-file-add-line',
    steps: [
      { title: '选择服务', desc: '在"查询服务"页面浏览所有可用服务，找到您需要的查询类型，点击"查看详情"了解服务内容和价格套餐。' },
      { title: '填写查询信息', desc: '在服务详情页选择合适的套餐，填写被查询对象的相关信息（如手机号、身份证号、微信号等），确保信息准确无误。' },
      { title: '确认并支付', desc: '核对订单信息和金额，选择支付方式（余额/USDT/客服支付），完成支付后订单自动进入处理队列。' },
      { title: '等待结果', desc: '支付成功后系统立即开始处理，您可在"我的订单"页面实时查看订单状态，结果出来后会有通知提醒。' },
    ],
  },
  pay: {
    title: '如何付款',
    icon: 'ri-bank-card-line',
    steps: [
      { title: '账户余额支付', desc: '注册登录后，前往"充值"页面为账户充值，充值成功后下单时选择"余额支付"即可，操作最为便捷快速。' },
      { title: 'USDT 支付', desc: '支持 TRC20/ERC20 网络的 USDT 转账支付，下单时选择"USDT支付"，系统会生成专属收款地址，转账后自动到账。' },
      { title: '联系客服支付', desc: '如以上方式不便，可联系在线客服，客服会为您提供其他支付方案，支持微信、支付宝等方式。' },
      { title: '支付注意事项', desc: '请勿向非官方地址转账，谨防诈骗。如遇支付问题请第一时间联系客服，我们会协助您处理。' },
    ],
  },
  result: {
    title: '如何查看结果',
    icon: 'ri-file-search-line',
    steps: [
      { title: '进入我的订单', desc: '登录账户后，点击顶部导航"我的订单"，找到对应的已完成订单。' },
      { title: '点击查看结果', desc: '在订单卡片上点击"查看结果"按钮，即可查看完整的查询报告内容。' },
      { title: '下载或截图保存', desc: '结果页面支持一键下载 PDF 报告，或直接截图保存，建议及时保存，结果保留 30 天。' },
      { title: '结果异议处理', desc: '如对查询结果有疑问，可在结果页面点击"申请复查"，我们会安排专业人员重新核实。' },
    ],
  },
  usdt: {
    title: '如何购买USDT',
    icon: 'ri-coin-line',
    steps: [
      { title: '选择交易平台', desc: '推荐使用欧易(OKX)、币安(Binance)等主流交易所购买 USDT，注册实名认证后即可交易。' },
      { title: '完成实名认证', desc: '根据平台要求完成 KYC 实名认证，上传身份证照片，通常几分钟内审核完成。' },
      { title: '购买 USDT', desc: '选择"C2C 交易"或"快捷买币"，选择 USDT，输入购买金额，通过支付宝/微信/银行卡完成付款。' },
      { title: '提币到钱包', desc: '购买成功后，将 USDT 提币到您的个人钱包地址，再用于本平台充值支付，注意选择正确的网络（TRC20 手续费更低）。' },
    ],
  },
  refund: {
    title: '如何退款',
    icon: 'ri-refund-2-line',
    steps: [
      { title: '退款条件', desc: '以下情况可申请退款：查询结果为空或无效、系统错误导致订单失败、服务说明与实际不符。已成功出结果的订单不支持退款。' },
      { title: '申请退款', desc: '进入"我的订单"，找到需要退款的订单，点击"申请退款"，选择退款原因并提交申请。' },
      { title: '审核周期', desc: '客服团队会在 1–3 个工作日内审核您的退款申请，审核期间可能会联系您了解详情。' },
      { title: '退款到账', desc: '审核通过后，退款将原路返回至您的账户余额，通常即时到账，余额可用于下次消费或提现。' },
    ],
  },
  contact: {
    title: '如何联系客服',
    icon: 'ri-customer-service-2-line',
    steps: [
      { title: '在线客服', desc: '点击页面右下角的客服图标，即可发起在线对话，客服团队 7×24 小时在线，平均响应时间不超过 3 分钟。' },
      { title: 'Telegram 联系', desc: '搜索我们的官方 Telegram 账号（@cha191a），发送消息即可，适合需要发送截图或文件的情况。' },
      { title: '微信联系', desc: '扫描页面底部的微信二维码添加客服微信，工作时间（9:00–22:00）内优先响应。' },
      { title: '注意事项', desc: '请勿相信任何主动联系您的"客服"，官方客服不会主动索要密码或要求转账，谨防诈骗。' },
    ],
  },
  referral: {
    title: '如何推广赚钱',
    icon: 'ri-gift-line',
    steps: [
      { title: '获取推广链接', desc: '登录账户后，进入"推广赚钱"页面，复制您的专属推广链接或二维码，分享给好友。' },
      { title: '好友下单返佣', desc: '好友通过您的链接注册并下单，您将获得订单金额 10%–35% 的佣金，佣金比例随推广业绩提升。' },
      { title: '查看佣金明细', desc: '在推广后台可实时查看推广人数、订单数量、佣金明细等数据，数据每小时更新一次。' },
      { title: '申请提现', desc: '佣金累计满 100 元即可申请提现，支持 USDT 和银行转账，1–2 个工作日到账。' },
    ],
  },
};

export default function HelpPage() {
  const location = useLocation();

  function getNavFromLocation(): NavKey {
    const tab = new URLSearchParams(location.search).get('tab');
    const tabKey = (tab ?? '').toLowerCase();

    if (tabKey) {
      const TAB_TO_NAV: Partial<Record<string, NavKey>> = {
        home: 'home',
        order: 'order',
        pay: 'pay',
        result: 'result',
        usdt: 'usdt',
        refund: 'refund',
        contact: 'contact',
        referral: 'referral',
      };
      const mapped = TAB_TO_NAV[tabKey];
      if (mapped) {
        return mapped;
      }
    }

    const path = location.pathname;
    if (path === '/help' || path === '/help/') {
      return 'home';
    }
    if (path === '/help/how-to-order') {
      return 'order';
    }
    if (path === '/help/how-to-pay') {
      return 'pay';
    }
    if (path === '/help/how-to-view-result') {
      return 'result';
    }
    if (path === '/help/referral') {
      return 'referral';
    }

    return 'home';
  }

  const [activeNav, setActiveNav] = useState<NavKey>(() => getNavFromLocation());
  useEffect(() => {
    setActiveNav(getNavFromLocation());
  }, [location.pathname, location.search]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const content = CONTENT_MAP[activeNav];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Page title row */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">帮助中心</h1>
            {/* Mobile nav toggle */}
            <button
              className="lg:hidden flex items-center space-x-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-menu-2-line" />
              </div>
              <span>目录</span>
            </button>
          </div>

          {/* Mobile nav dropdown */}
          {mobileNavOpen && (
            <div className="lg:hidden bg-white rounded-2xl border border-gray-100 p-3 mb-5 grid grid-cols-2 gap-1">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.key}
                  onClick={() => { setActiveNav(item.key); setMobileNavOpen(false); }}
                  className={`flex items-center space-x-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer text-left ${
                    activeNav === item.key
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-sm`} />
                  </div>
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

            {/* Sidebar */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-100 p-3 sticky top-24">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2">帮助目录</p>
                <nav className="space-y-0.5">
                  {NAV_ITEMS.map((item) => {
                    const isActive = activeNav === item.key;
                    return (
                      <button
                        key={item.key}
                        onClick={() => setActiveNav(item.key)}
                        className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer text-left ${
                          isActive
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-sm`} />
                        </div>
                        <span>{item.label}</span>
                        {isActive && (
                          <div className="ml-auto w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-right-s-line text-white/70" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="lg:col-span-3 space-y-5">

              {activeNav === 'home' ? (
                <>
                  {/* Search bar */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">您好，有什么可以帮您？</h2>
                    <p className="text-gray-400 text-sm mb-4">搜索常见问题，或从下方快速导航选择</p>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-search-2-line text-gray-400" />
                        </div>
                      </div>
                      <input
                        type="text"
                        placeholder="搜索帮助内容，如：如何退款"
                        className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  {/* Quick nav cards */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <h3 className="text-base font-bold text-gray-900 mb-4">快速导航</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {QUICK_CARDS.map((card) => (
                        <button
                          key={card.key}
                          onClick={() => setActiveNav(card.key as NavKey)}
                          className="group flex items-center space-x-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 cursor-pointer text-left border border-transparent hover:border-gray-200"
                        >
                          <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-100 group-hover:border-gray-200 transition-colors">
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={`${card.icon} text-gray-600 text-base`} />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">{card.label}</p>
                            <p className="text-xs text-gray-400 mt-0.5 truncate">{card.desc}</p>
                          </div>
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                            <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-gray-500 transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Stats bar */}
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: '平均响应时间', value: '< 3 分钟', icon: 'ri-time-line' },
                      { label: '问题解决率', value: '99.2%', icon: 'ri-checkbox-circle-line' },
                      { label: '用户满意度', value: '4.9 / 5', icon: 'ri-star-line' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                          <i className={`${stat.icon} text-xl text-gray-400`} />
                        </div>
                        <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* FAQ */}
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-50">
                      <h3 className="text-base font-bold text-gray-900">常见问题</h3>
                      <p className="text-xs text-gray-400 mt-0.5">点击问题查看详细解答</p>
                    </div>
                    <FaqAccordion items={FAQ_ITEMS} />
                  </div>

                  {/* Contact CTA */}
                  <div className="bg-gray-900 rounded-2xl p-6 text-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-customer-service-2-line text-white text-xl" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-base font-semibold">找不到您需要的帮助？</h3>
                          <p className="text-gray-400 text-sm mt-0.5">我们的客服团队 7×24 小时随时为您服务</p>
                        </div>
                      </div>
                      <button
                        onClick={() => openSaleSmartlyChat()}
                        className="flex-shrink-0 inline-flex items-center justify-center space-x-2 px-5 py-2.5 bg-white text-gray-900 rounded-xl hover:bg-gray-100 transition-colors font-medium text-sm cursor-pointer whitespace-nowrap"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-chat-1-line" />
                        </div>
                        <span>立即联系客服</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Article header */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <button
                        onClick={() => setActiveNav('home')}
                        className="flex items-center space-x-1 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer text-sm"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-left-line" />
                        </div>
                        <span>返回</span>
                      </button>
                      <span className="text-gray-200">/</span>
                      <span className="text-sm text-gray-500">{content.title}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${content.icon} text-gray-600 text-lg`} />
                        </div>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">{content.title}</h2>
                    </div>
                  </div>

                  {/* Steps */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <div className="space-y-6">
                      {content.steps.map((step, i) => (
                        <div key={i} className="flex space-x-4">
                          <div className="flex-shrink-0 flex flex-col items-center">
                            <div className="w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {i + 1}
                            </div>
                            {i < content.steps.length - 1 && (
                              <div className="w-px flex-1 bg-gray-100 mt-2" />
                            )}
                          </div>
                          <div className="flex-1 pb-6">
                            <h4 className="text-sm font-semibold text-gray-900 mb-1.5">{step.title}</h4>
                            <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Related articles */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">相关帮助</h3>
                    <div className="space-y-1">
                      {NAV_ITEMS.filter((n) => n.key !== 'home' && n.key !== activeNav).slice(0, 4).map((item) => (
                        <button
                          key={item.key}
                          onClick={() => setActiveNav(item.key)}
                          className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
                        >
                          <div className="flex items-center space-x-2.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className={`${item.icon} text-gray-400 text-sm`} />
                            </div>
                            <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">{item.label}</span>
                          </div>
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-gray-500 transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Contact CTA */}
                  <div className="bg-gray-900 rounded-2xl p-5 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <p className="font-semibold text-sm">还有疑问？</p>
                      <p className="text-gray-400 text-xs mt-0.5">联系客服获取一对一帮助</p>
                    </div>
                    <button
                      onClick={() => openSaleSmartlyChat()}
                      className="flex-shrink-0 inline-flex items-center justify-center space-x-1.5 px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-chat-1-line" />
                      </div>
                      <span>联系客服</span>
                    </button>
                  </div>
                </>
              )}
            </main>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
